import { Typography, FormControlLabel } from "@mui/material";
import parse, {
  domToReact,
  HTMLReactParserOptions,
  DOMNode,
} from "html-react-parser";
import { decodeHtmlEntities } from "../Helper/Helper";
import { Field } from "../../../Interface/scheduler/form";
import ArrowCircleRightSharpIcon from "@mui/icons-material/ArrowCircleRightSharp";
import usePostQuote from "../../hooks/usePostQuote";
import "./NewArrowField.css";
import { useFormConfigContext } from "../../contexts/FormConfigContext";
import { sessionStorageUtils } from "../../../utility/utility";
import { SESSION_FORMVALUES } from "../../../constants/constant";
import { useFormContext } from "../../contexts/FormContext";
import isInboundFromNbly from "../../../utility/checkInBoundFromNbly";

interface NextArrowFieldProps {
  field: Field;
  disabled?: boolean;
}

const parseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (
      domNode.type === "tag" &&
      domNode.name === "a" &&
      domNode.attribs?.href
    ) {
      const url = domNode.attribs.href;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "underline", cursor: "pointer" }}
        >
          {domToReact(domNode.children as DOMNode[], parseOptions)}
        </a>
      );
    }
  },
};

const NextArrowField = ({ field, disabled = false }: NextArrowFieldProps) => {
  const { postQuote } = usePostQuote();
  const { formConfig } = useFormConfigContext();
  const { formValues } = useFormContext();
  const redirectionUrl = formConfig?.redirectionPageUrl ?? "/confirmation";

  const getLabelText = (): string => {
    return field.calabel || field.label || "";
  };

  const renderLabel = (labelText: string) => (
    <span data-testid="checkbox-label" className="Arrow-label">
      {parse(labelText, parseOptions)}
    </span>
  );

  const renderDescription = () => {
    const description = decodeHtmlEntities(
      field.cadescription || field.description || ""
    );
    if (!description) return null;

    return (
      <Typography
        component="div"
        className={`terms-policy-text ${field.id}-description`}
        variant="body2"
        data-testid="checkbox-description"
      >
        {parse(description, parseOptions)}
      </Typography>
    );
  };

  const handleNextArrowClick = async (
    e?: React.MouseEvent | React.KeyboardEvent
  ) => {
    if (disabled) {
      if (e) e.preventDefault();
      return;
    }
    try {
      await postQuote();
      sessionStorageUtils.setItem(SESSION_FORMVALUES, formValues);
      if (isInboundFromNbly()) {
        globalThis.location.replace(
          `${redirectionUrl}?leadconcept=${formConfig.conceptCode}`
        );
        return;
      }
      globalThis.location.replace(redirectionUrl);
    } catch (error) {
      console.error("Error posting quote:", error);
    }
  };

  const labelText = getLabelText();
  if (!labelText) return null;

  return (
    <div>
      <button
        className={`input-label-wrap checkbox${disabled ? " disabled" : ""}`}
        id={field.id}
        key={field.id}
        style={{
          flex: field.width === "100%" ? "inherit" : undefined,
          gap: "8px",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        data-testid="checkbox-field-root"
        onClick={handleNextArrowClick}
        disabled={disabled}
        aria-label={`Next step: ${labelText.replaceAll(/<[^>]*>?/gm, "")}`}
        aria-disabled={disabled}
        type="button"
      >
        <FormControlLabel
          className="checkbox-next-arrow-field"
          control={
            <ArrowCircleRightSharpIcon
              fontSize="small"
              className="arrow-icon"
              color={disabled ? "disabled" : "primary"}
            />
          }
          label={renderLabel(labelText)}
        />
      </button>
      {renderDescription()}
    </div>
  );
};

export default NextArrowField;
